import { PersonEntity } from '../types/PersonEntity';
import { InfrastructureEntity } from '../types/InfrastructureEntity';
import { StorageEntity } from '../types/StorageEntity';
import { PipelineEntity } from '../types/PipelineEntity';
import { UpgradeEntity } from '../types/UpgradeEntity';
import { AchievementEntity } from '../types/AchievementEntity';
import { EventEntity } from '../types/EventEntity';
import { defineEvents } from './events';
import { defineAchievements } from './achievements';
import { defineInfrastructure } from './infrastructure';
import { defineUpgrades } from './upgrades';
import { definePersons } from './persons';
import type { GameState, ResourceBundle } from '@/types';
import { GAME_CONFIG } from '@/config/gameConfig';
import { stages } from './stages';
import { ProductionRateBundle, StorageCapacityBundle, ExpenseBundle } from '@/types/EntityTypes';


// ============================================================================
// ENTITY MANAGER
// ============================================================================

export class EntityManager {
  public persons: PersonEntity[];
  public infrastructure: (InfrastructureEntity | StorageEntity | PipelineEntity)[];
  public events: EventEntity[];
  public upgrades: UpgradeEntity[];
  public achievements: AchievementEntity[];

  constructor() {
    this.persons = definePersons();
    this.infrastructure = defineInfrastructure();
    this.events = defineEvents();
    this.upgrades = defineUpgrades();
    this.achievements = defineAchievements();
  }

  // Helper methods
  getPersonById(id: string): PersonEntity | null {
    return this.persons.find(person => person.id === id) || null;
  }

  getInfrastructureById(id: string): (InfrastructureEntity | StorageEntity | PipelineEntity) | null {
    return this.infrastructure.find(inf => inf.id === id) || null;
  }

  getUpgradeById(id: string): UpgradeEntity | null {
    return this.upgrades.find(upgrade => upgrade.id === id) || null;
  }

  getAchievementById(id: string): AchievementEntity | null {
    return this.achievements.find(achievement => achievement.id === id) || null;
  }

  getAvailablePersons(gameState: GameState): PersonEntity[] {
    return PersonEntity.getAvailablePersons(this.persons, gameState);
  }

  getAvailableInfrastructure(gameState: GameState): (InfrastructureEntity | StorageEntity | PipelineEntity)[] {
    return this.infrastructure.filter(inf => inf.meetsNonPersonnelRequirements(gameState));
  }

  getPossibleEvents(gameState: GameState): EventEntity[] {
    return EventEntity.getPossibleEvents(this.events, gameState);
  }

  getAvailableUpgrades(gameState: GameState): UpgradeEntity[] {
    return UpgradeEntity.getAvailableUpgrades(this.upgrades, gameState);
  }

  getNewAchievements(state: GameState): AchievementEntity[] {
    return AchievementEntity.getNewAchievements(this.achievements, state);
  }

  // ============================================================================
  // PIPELINE STAGES
  // ============================================================================

  getPipelineStageInfrastructure(stageId: string): string[] {
    return stages[stageId as keyof typeof stages]?.infrastructure || [];
  }

  getPipelineStageTitle(stageId: string): string {
    return stages[stageId as keyof typeof stages]?.title || '';
  }

  getPipelineStageActions(stageId: string): any[] {
    return stages[stageId as keyof typeof stages]?.actions || [];
  }

  // ============================================================================
  // ENTITY CALCULATIONS
  // ============================================================================

  calculateTotalExpenses(gameState: GameState): ExpenseBundle {
    const expenses = new ExpenseBundle();

    // Calculate personnel expenses
    this.persons.forEach(person => {
      const count = gameState.personnel[person.getStatePropertyName() as keyof typeof gameState.personnel] as number;
      if (count > 0 && person.consumes?.money) {
        expenses.talentExpensesPerSec += person.consumes.money * count;
      }
    });

    // Calculate infrastructure expenses
    this.infrastructure.forEach(inf => {
      const count = gameState.infrastructure[inf.getStatePropertyName() as keyof typeof gameState.infrastructure] as number;
      if (count > 0 && inf.consumes?.money) {
        expenses.infrastructureExpensesPerSec += inf.consumes?.money * count;
      }
    });

    expenses.totalExpensesPerSec = expenses.talentExpensesPerSec + expenses.infrastructureExpensesPerSec;

    return expenses;
  }

  /**
   * Calculate total production from all entities
   */
  calculateTotalProduction(gameState: GameState): ProductionRateBundle {
    const production = new ProductionRateBundle();

    // Calculate production from infrastructure
    this.infrastructure.forEach(inf => {
      const count = gameState.infrastructure[inf.getStatePropertyName() as keyof typeof gameState.infrastructure] as number;
      if (count > 0 && inf.produces && inf.canBeBuilt(gameState)) {
        // Apply production with reliability
        const reliability = 1 - gameState.infrastructureFailureRate;
        if (inf.produces.rawData) production.rawData = (production.rawData || 0) + inf.produces.rawData * count * reliability;
        if (inf.produces.cleanData) production.cleanData = (production.cleanData || 0) + inf.produces.cleanData * count * reliability;
        if (inf.produces.feature) production.feature = (production.feature || 0) + inf.produces.feature * count * reliability;
        if (inf.produces.model) production.model = (production.model || 0) + inf.produces.model * count * reliability;
        if (inf.produces.reasoningEngine) production.reasoningEngine = (production.reasoningEngine || 0) + inf.produces.reasoningEngine * count * reliability;
        if (inf.produces.money) production.money = (production.money || 0) + inf.produces.money * count * reliability;
        if (inf.produces.agiScore) production.agiScore = (production.agiScore || 0) + inf.produces.agiScore * count * reliability;
      }
    });
    
    // Calculate production from personnel
    this.persons.forEach(person => {
      const count = gameState.personnel[person.getStatePropertyName() as keyof typeof gameState.personnel] as number;
      if (count > 0 && person.produces) {
        if (person.produces.rawData) production.rawData = (production.rawData || 0) + person.produces.rawData * count;
        if (person.produces.cleanData) production.cleanData = (production.cleanData || 0) + person.produces.cleanData * count;
        if (person.produces.feature) production.feature = (production.feature || 0) + person.produces.feature * count;
        if (person.produces.model) production.model = (production.model || 0) + person.produces.model * count;
        if (person.produces.reasoningEngine) production.reasoningEngine = (production.reasoningEngine || 0) + person.produces.reasoningEngine * count;
        if (person.produces.money) production.money = (production.money || 0) + person.produces.money * count;
        if (person.produces.agiScore) production.agiScore = (production.agiScore || 0) + person.produces.agiScore * count;
      }
    });

    return production;
  }

  /**
   * Calculate total storage capacity from all entities
   */
  calculateTotalStorageCapacity(gameState: GameState): StorageCapacityBundle {
    const storage = new StorageCapacityBundle()
    storage.rawData = 50;
    storage.cleanData = 50;
    storage.feature = 25;
    storage.model = 1;
    storage.reasoningEngine = 1; // Base storage
    storage.researchProgress = 0;

    // Add storage from infrastructure
    this.infrastructure.forEach(inf => {
      const count = gameState.infrastructure[inf.getStatePropertyName() as keyof typeof gameState.infrastructure] as number;
      if (count > 0 && inf.storage) {
        if (inf.storage.rawData) storage.rawData = (storage.rawData || 0) + inf.storage.rawData * count;
        if (inf.storage.cleanData) storage.cleanData = (storage.cleanData || 0) + inf.storage.cleanData * count;
        if (inf.storage.feature) storage.feature = (storage.feature || 0) + inf.storage.feature * count;
        if (inf.storage.model) storage.model = (storage.model || 0) + inf.storage.model * count;
        if (inf.storage.reasoningEngine) storage.reasoningEngine = (storage.reasoningEngine || 0) + inf.storage.reasoningEngine * count;
      }
    });

    // Add storage from personnel
    // Each AI Researcher adds 50 research points storage
    const aiResearchers = gameState.personnel.aiResearcher || 0;
    if (aiResearchers > 0) {
      storage.researchProgress = (storage.researchProgress || 0) + (aiResearchers * 50);
    }

    // Apply storage capacity multipliers from upgrades
    const multipliers = gameState.storageCapacityMultipliers || {};
    if (multipliers.rawData) storage.rawData = (storage.rawData || 0) * multipliers.rawData;
    if (multipliers.cleanData) storage.cleanData = (storage.cleanData || 0) * multipliers.cleanData;
    if (multipliers.feature) storage.feature = (storage.feature || 0) * multipliers.feature;
    if (multipliers.model) storage.model = (storage.model || 0) * multipliers.model;
    if (multipliers.reasoningEngine) storage.reasoningEngine = (storage.reasoningEngine || 0) * multipliers.reasoningEngine;
    if (multipliers.researchProgress) storage.researchProgress = (storage.researchProgress || 0) * multipliers.researchProgress;

    return storage;
  }

  /**
   * Calculate infrastructure failure rate
   */
  calculateInfrastructureFailureRate(gameState: GameState): number {
    let totalFailureRate = GAME_CONFIG.CONSTANTS.MIN_FAILURE_RATE;
    
    this.infrastructure.forEach(inf => {
      const count = gameState.infrastructure[inf.getStatePropertyName() as keyof typeof gameState.infrastructure] as number;
      if (count > 0) {
        // Use the infrastructure's own failure rate calculation
        totalFailureRate += inf.getFailureRateContribution(count);
      }
    });

    // Apply cloud upgrade effect if purchased
    const multiplier = gameState.failureRateMultiplier || 1;
    totalFailureRate *= multiplier;

    // Ensure failure rate stays within bounds
    return Math.max(GAME_CONFIG.CONSTANTS.MIN_FAILURE_RATE, Math.min(GAME_CONFIG.CONSTANTS.MAX_FAILURE_RATE, totalFailureRate));
  }

  getResourceProperty(resource: string): keyof GameState['resources'] {
    return resource as keyof GameState['resources'];
  }

  getStorageCapacityProperty(resource: string): keyof GameState['storageCapacity'] {
    return resource as keyof GameState['storageCapacity'];
  }

  isStorageFull(gameState: GameState, resource: string): boolean {
    const currentValue = gameState.resources[this.getResourceProperty(resource)] as number;
    const capacity = gameState.storageCapacity[this.getStorageCapacityProperty(resource)] as number;
    return currentValue >= capacity;
  }

  hasFullStorageForProduction(gameState: GameState, produces: ResourceBundle): boolean {
    return Object.entries(gameState.resources).some(([resourceKey]) =>
      (resourceKey in produces) &&
      this.isStorageFull(gameState, resourceKey as keyof ResourceBundle)
    );
  }

  applyEntityProductionAndConsumption(gameState: GameState, timeStep: number) {
    // Handle infrastructure
    this.infrastructure.forEach(inf => {
      const propertyName = inf.getStatePropertyName();
      const count = gameState.infrastructure[propertyName] as number;
      if (count > 0 && inf.produces) {
        for (let i = 0; i < count; i++) {
          // Check if can consume (excluding money - that's handled by expenses)
          let canProduce = true;
          if (inf.consumes) {
            canProduce = Object.entries(inf.consumes).every(
              ([resource, amount]) => {
                // Skip money check - money is always consumed via expenses
                if (resource === 'money') return true;
                return (gameState.resources[resource as keyof typeof gameState.resources] || 0) >= (amount as number) * timeStep;
              }
            );
          }

          // Check if enough storage
          if (this.hasFullStorageForProduction(gameState, inf.produces)) {
            canProduce = false;
          }

          // Debug logging for training pipeline
          if (canProduce) {
            // Consume (excluding money - that's handled by expenses)
            if (inf.consumes) {
              Object.entries(inf.consumes).forEach(([resource, amount]) => {
                // Skip money consumption - it's handled via the expense system
                if (resource === 'money') return;
                gameState.resources[resource as keyof typeof gameState.resources] =
                  Math.max(0, (gameState.resources[resource as keyof typeof gameState.resources] || 0) - (amount as number) * timeStep);
              });
            }
            // Produce (apply infrastructure reliability)
            const reliability = 1 - gameState.infrastructureFailureRate;
            Object.entries(inf.produces).forEach(([resource, amount]) => {
              const cap = gameState.storageCapacity[resource as keyof typeof gameState.storageCapacity] || Infinity;
              const productionAmount = (amount as number) * timeStep * reliability;
              gameState.resources[resource as keyof typeof gameState.resources] =
                Math.min(
                  cap,
                  (gameState.resources[resource as keyof typeof gameState.resources] || 0) + productionAmount
                );
            });
          }
        }
      }
    });
  
    // Handle personnel
    this.persons.forEach(person => {
      const propertyName = person.getStatePropertyName();
      const count = gameState.personnel[propertyName] as number;
      if (count > 0 && person.produces) {
        for (let i = 0; i < count; i++) {
          // Check if can consume (excluding money - that's handled by expenses)
          let canProduce = true;
          if (person.consumes) {
            canProduce = Object.entries(person.consumes).every(
              ([resource, amount]) => {
                // Skip money check - money is always consumed via expenses
                if (resource === 'money') return true;
                return (gameState.resources[resource as keyof typeof gameState.resources] || 0) >= (amount as number) * timeStep;
              }
            );
          }
          if (canProduce && person.produces) {
            canProduce = Object.entries(person.produces).every(
              ([resource, _amount]) => {
                const cap = gameState.storageCapacity[resource as keyof typeof gameState.storageCapacity] || Infinity;
                return (gameState.resources[resource as keyof typeof gameState.resources] || 0) < cap;
              }
            );
          }

          // Debug logging for reasoning specialist
          if (canProduce) {
            // Consume (excluding money - that's handled by expenses)
            if (person.consumes) {
              Object.entries(person.consumes).forEach(([resource, amount]) => {
                // Skip money consumption - it's handled via the expense system
                if (resource === 'money') return;
                gameState.resources[resource as keyof typeof gameState.resources] =
                  Math.max(0, (gameState.resources[resource as keyof typeof gameState.resources] || 0) - (amount as number) * timeStep);
              });
            }
            // Produce
            Object.entries(person.produces).forEach(([resource, amount]) => {
              const cap = gameState.storageCapacity[resource as keyof typeof gameState.storageCapacity] || Infinity;
              gameState.resources[resource as keyof typeof gameState.resources] =
                Math.min(
                  cap,
                  (gameState.resources[resource as keyof typeof gameState.resources] || 0) + (amount as number) * timeStep
                );
            });
          }
        }
      }
    });
  
    // Round to 8 decimal places to avoid floating point overflow while preserving small values
    Object.keys(gameState.resources).forEach(resource => {
      gameState.resources[resource as keyof typeof gameState.resources] =
        Math.round((gameState.resources[resource as keyof typeof gameState.resources] || 0) * 100000000) / 100000000;
    });
  }
}